import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useLang, locProduct, shopPath, Approx } from "@/lib/i18n";
import { Minus, Plus, Check, Truck, ShieldCheck, Palmtree, ArrowRight } from "lucide-react";
import { useProduct, useProducts } from "@/hooks/useProducts";
import { useCart } from "@/context/CartContext";
import { euro } from "@/lib/api";
import { MaskLine } from "@/components/site/Reveal";
import { ProductCard } from "@/components/site/ProductCard";
import { ProductDetails } from "@/components/site/ProductDetails";
import { ProductZoom } from "@/components/site/ProductZoom";
import { StickyBuyBar } from "@/components/site/StickyBuyBar";
import { useSeo, absUrl, SITE_URL, shippingDetails, RETURN_POLICY } from "@/lib/seo";
import { collectionOf } from "@/lib/collections";
import { CoachProductBox } from "@/components/site/Coach";
import { SubProductBox, BuyChoice } from "@/components/subscription/SubPromo";
import { ExtremePromo } from "@/components/subscription/ExtremeOffer";
import { DeliveryEstimate } from "@/components/site/DeliveryEstimate";
import { trackSubCta } from "@/components/subscription/SubOffers";
import { EXTREME, isSubEligible } from "@/lib/subscription";
import { PayLaterMessage, WhatsAppAdvice } from "@/components/site/Conversion";
import { ReviewForm } from "@/components/site/ReviewForm";
import { PaymentLogos } from "@/components/site/Trust";
import { SHOP } from "@/lib/shop";
import { ProductReviews, useReviews } from "@/components/site/ProductReviews";
import { Testimonials } from "@/components/home/Testimonials";
import { trackProductView } from "@/lib/knownEmail";
import { ExpertiseBlock } from "@/components/site/Expertise";
import { PregnancyNotice, hasRepouss } from "@/components/site/PregnancyNotice";
import { FlashPrice, GiftNote } from "@/components/site/FlashSale";
import { ShopRating, ShopReviewsHighlight } from "@/components/site/ShopReviews";
import { FormationProductBox } from "@/components/site/FormationBox";
import { useShipGeo, productShipNote } from "@/lib/shipGeo";
import { PreorderStatus, LaunchOffer, SerumDetails } from "@/components/site/Preorder";
import { ProductVideo } from "@/components/site/ProductVideo";
import { guidesFor } from "@/lib/topics";

const HUB = { Pousse: ["/pousse-des-cheveux", "Pousse des cheveux"], Locks: ["/locks", "Entretien des locks"], Barbe: ["/barbe", "Soin de la barbe"], Kids: ["/cheveux-enfants", "Cheveux des enfants"] };

const clip = (s, n = 155) => { const t = String(s || "").replace(/\s+/g, " ").trim(); return t.length > n ? `${t.slice(0, n - 1).replace(/\s+\S*$/, "")}…` : t; };

function ratingLd(p, reviews = []) {
  if (!p.rating_count) return {};
  return {
    aggregateRating: { "@type": "AggregateRating", ratingValue: p.rating_avg, reviewCount: p.rating_count, bestRating: 5, worstRating: 1 },
    review: reviews.slice(0, 10).map((r) => ({
      "@type": "Review", author: { "@type": "Person", name: r.name }, datePublished: r.received_at, reviewBody: r.text,
      reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5, worstRating: 1 },
    })),
  };
}

function productLd(p, reviews) {
  const url = `${SITE_URL}/produit/${p.slug}`;
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Product", "@id": `${url}#product`, name: p.name, sku: p.slug, url, image: [absUrl(p.image), ...(Array.isArray(p.gallery) ? p.gallery.map(absUrl) : [])].filter(Boolean),
        description: clip(p.description || p.tagline, 500), category: p.category, brand: { "@type": "Brand", name: "Ozentya" }, ...ratingLd(p, reviews),
        offers: {
          "@type": "Offer", url, priceCurrency: "EUR", price: Number(p.price).toFixed(2), itemCondition: "https://schema.org/NewCondition",
          availability: p.preorder ? "https://schema.org/PreOrder" : p.stock === 0 ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
          ...(p.preorder && p.preorder_date ? { availabilityStarts: String(p.preorder_date).slice(0, 10) } : {}),
          seller: { "@type": "Organization", name: "Ozentya" }, shippingDetails: shippingDetails(p.price), hasMerchantReturnPolicy: RETURN_POLICY,
        },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Accueil", item: `${SITE_URL}/` },
          { "@type": "ListItem", position: 2, name: "Boutique", item: `${SITE_URL}/boutique` },
          { "@type": "ListItem", position: 3, name: p.name, item: url },
        ],
      },
      ...(Array.isArray(p.faq) && p.faq.length ? [{
        "@type": "FAQPage", "@id": `${url}#faq`,
        mainEntity: p.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
      }] : []),
    ],
  };
}

function Thumbs({ p, shown, onPick }) {
  const list = [p.image, ...(Array.isArray(p.gallery) ? p.gallery : [])].filter(Boolean);
  if (list.length < 2) return null;
  return (
    <div data-testid="product-thumbs" className="relative mt-4 flex gap-3">
      {list.map((src, k) => (
        <button key={src} type="button" onClick={() => onPick(src)} data-testid={`product-thumb-${k}`} aria-label={`Photo ${k + 1}`}
          className={`h-20 w-20 overflow-hidden rounded-sm border bg-[var(--ink-2)] transition-[border-color] duration-300 ${shown === src ? "border-[var(--gold)]" : "border-[var(--line)] hover:border-[var(--gold)]/60"}`}>
          <img src={src} alt="" loading="lazy" className="h-full w-full object-cover" />
        </button>
      ))}
    </div>
  );
}

const firstLine = (s) => { const f = String(s || "").split(/(?<=[.!])\s/)[0].trim(); return f.length > 170 ? `${f.slice(0, 167).trimEnd()}…` : f; };

/** Sous la photo texture : une ligne « comment l'appliquer » par soin (texte usage des fiches). */
function HowToApply({ p }) {
  const { t } = useLang();
  const { data } = useProducts({});
  const all = Array.isArray(data) ? data : [];
  const rows = p.components?.length
    ? p.components.map((c) => all.find((x) => x.slug === c.slug)).filter((x) => x?.usage).map((x) => [x.name, x.usage])
    : p.usage ? [[null, p.usage]] : [];
  if (!rows.length) return null;
  return (
    <div data-testid="product-how-to-apply" className="relative mt-4 rounded-sm border border-[var(--line)] bg-[var(--ink-2)] p-4">
      <p className="eyebrow mb-2">{t("Comment l'appliquer")}</p>
      <ul className="space-y-1.5 text-sm leading-relaxed text-[var(--parchment)]/90">
        {rows.map(([name, usage], i) => (
          <li key={name || i} data-testid={`how-to-apply-${i}`}>{name ? <span className="text-[var(--gold-soft)]">{name} : </span> : null}{firstLine(usage)}</li>
        ))}
      </ul>
    </div>
  );
}

function ProductGuides({ slug }) {
  const guides = guidesFor(slug);
  if (!guides.length) return null;
  return (
    <aside data-testid="product-guides" className="mt-8 rounded-sm border border-[var(--line)] bg-[var(--ink-2)] p-5">
      <p className="eyebrow mb-3">Bien l'utiliser : nos guides</p>
      <ul className="space-y-2">
        {guides.map((g) => (
          <li key={g.path}>
            <Link to={g.path} data-testid={`product-guide-${g.path.slice(1)}`} className="inline-flex items-start gap-2 text-sm text-[var(--parchment)] hover:text-[var(--gold-soft)]">
              <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-[var(--gold)]" />{g.h1}
            </Link>
          </li>
        ))}
      </ul>
    </aside>
  );
}

function Related({ p }) {
  const { t } = useLang();
  const { data } = useProducts({ category: p.category });
  const list = (Array.isArray(data) ? data : []).filter((x) => x.slug !== p.slug).slice(0, 4);
  if (!list.length) return null;
  return (
    <section className="mt-32">
      <p className="eyebrow mb-4">{t("Dans la même gamme")}</p>
      <h2 className="mb-12 font-display text-4xl italic">{t("Vous aimerez aussi")}</h2>
      <div className="grid grid-cols-1 gap-x-6 gap-y-14 sm:grid-cols-2 lg:grid-cols-4">
        {list.map((x, i) => <ProductCard key={x.slug} p={x} index={i} />)}
      </div>
    </section>
  );
}

export default function ProductPage() {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const { data: raw, isLoading, isError } = useProduct(slug);
  const p = locProduct(raw, lang);
  const en = lang === "en";
  const { add } = useCart();
  const [qty, setQty] = useState(1);
  const [mode, setMode] = useState("once");
  const [pick, setShown] = useState(null);
  const shown = pick && (pick === p?.image || (p?.gallery || []).includes(pick)) ? pick : p?.image;
  const reviews = useReviews(slug);
  const geo = useShipGeo();
  const viewed = p?.slug;
  useEffect(() => { trackProductView(viewed); setMode("once"); }, [viewed]);
  useSeo(p ? (en ? {
    title: `${p.name} | Ozentya`, lang: "en",
    description: clip(`${p.name}: ${p.tagline || p.description || ""} ${euro(p.price)} incl. VAT, shipped worldwide from France.`),
    path: `/en/product/${p.slug}`, image: p.image, type: "product", jsonLd: productLd(p, reviews),
  } : {
    title: p.seo_title || `${p.name} — ${p.category === "Pousse" ? "Pousse et repousse cheveux afro" : `${p.type === "pack" ? "Pack" : "Soin"} ${p.category}`} | Ozentya`,
    description: p.seo_description || clip(`${p.name} : ${p.tagline || p.description || ""} ${euro(p.price)} TTC, livraison gratuite en Point Relais Mondial Relay.`),
    path: `/produit/${p.slug}`, image: p.image, type: "product", jsonLd: productLd(p, reviews),
  }) : { title: isError ? "Soin introuvable | Ozentya" : undefined, noindex: true });

  if (isLoading) return <main className="min-h-screen pt-40 text-center text-[var(--muted)]">{t("Chargement…")}</main>;
  if (isError || !p)
    return (
      <main data-testid="product-not-found" className="min-h-screen px-5 pt-40 text-center">
        <p className="font-display text-4xl italic">{t("Ce soin est introuvable.")}</p>
        <Link to={shopPath(lang)} className="mt-8 inline-block text-[var(--gold-soft)] underline">{t("Retour à la boutique")}</Link>
      </main>
    );

  const duoOk = !en && !p.box && isSubEligible(p);
  return (
    <main data-testid="product-page" className="mx-auto max-w-[1440px] overflow-x-clip px-5 pb-32 pt-40 md:px-10">
      <Link to={shopPath(lang)} data-testid="product-back-link" className="text-xs uppercase tracking-[0.24em] text-[var(--muted)] hover:text-[var(--gold-soft)]">{t("← Boutique")}</Link>
      {collectionOf(p) && !en ? <Link to={collectionOf(p).path} data-testid="product-collection-link" className="ml-4 text-xs uppercase tracking-[0.24em] text-[var(--gold-soft)] hover:underline">· Gamme {collectionOf(p).name}</Link> : null}
      {HUB[p.category] && !en ? <Link to={HUB[p.category][0]} data-testid="product-hub-link" className="ml-4 text-xs uppercase tracking-[0.24em] text-[var(--gold-soft)] hover:underline">· Conseils : {HUB[p.category][1]}</Link> : null}
      <div className="mt-8 grid gap-14 lg:grid-cols-12">
        <div className="relative self-start lg:sticky lg:top-36 lg:col-span-7">
          <div className="absolute -inset-4 bg-[radial-gradient(circle_at_50%_35%,rgba(217,180,106,0.2),transparent_65%)]" />
          <div className="relative aspect-square overflow-hidden rounded-sm border border-[var(--line)] bg-[var(--ink-2)]">
            <img src={shown} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full scale-125 object-cover opacity-45 blur-3xl" />
            <ProductZoom key={shown} src={shown} alt={shown === p.image ? p.name : shown.includes("/textures/") ? `${p.name} — ${en ? "texture illustration" : "illustration de la texture"}` : `${p.name} — ${en ? "campaign photo" : "photo de campagne"}`} />
          </div>
          <Thumbs p={p} shown={shown} onPick={setShown} />
          {shown?.includes("/products/textures/") ? <HowToApply p={p} /> : null}
          {p.video ? <ProductVideo p={p} testid="product-video" className="relative mx-auto mt-6 max-w-[340px]" /> : null}
        </div>
        <div className="lg:col-span-5 lg:pt-6">
          <p className="eyebrow mb-5">{t(p.category)} · {t(p.type === "pack" ? "Pack Ozentya" : "À l'unité")}{p.volume ? <span data-testid="product-volume"> · {p.volume}</span> : null}</p>
          {p.launch_offer || p.preorder ? (
            <div className="mb-5 flex flex-wrap gap-2">
              {p.badge ? <span data-testid="product-badge-new" className="rounded-full bg-[var(--gold)] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-[var(--ink)]">{p.badge}</span> : null}
              {p.launch_offer ? <span data-testid="product-badge-launch" className="rounded-full border border-[var(--gold)] px-3 py-1 text-[11px] uppercase tracking-[0.2em] text-[var(--gold-soft)]">{en ? "Launch offer" : "Offre de lancement"}</span> : null}
            </div>
          ) : null}
          <h1 data-testid="product-title" className="font-display text-4xl leading-[1] md:text-6xl"><MaskLine delay={0.2}>{p.name}</MaskLine></h1>
          <div className="mt-8 flex flex-wrap items-baseline gap-x-4 gap-y-2">
            <span data-testid="product-detail-price" className="font-display text-5xl text-[var(--gold-soft)]">{euro(p.price)}</span>
            {p.compare_at ? <span className="text-lg text-[var(--muted)] line-through">{euro(p.compare_at)}</span> : null}
            {p.compare_at > p.price ? <span data-testid="product-savings" className="rounded-full border border-[var(--gold)]/50 px-3 py-1 text-xs uppercase tracking-[0.16em] text-[var(--gold-soft)]">{t("Économisez")} {euro(p.compare_at - p.price)}</span> : null}
          </div>
          <Approx eur={p.price} className="mt-1 block" />
          <p data-testid="product-price-note" className="mt-2 text-xs text-[var(--muted)]">{productShipNote(geo, lang)}</p>
          <FlashPrice price={p.price} />
          {en ? null : <GiftNote slug={p.slug} />}
          {p.preorder ? <PreorderStatus p={p} /> : null}
          {p.preorder ? null : <div className="mt-4"><ShopRating testid="product-shop-rating" /></div>}
          <PayLaterMessage amount={p.price} />
          <p className="mt-8 text-base leading-relaxed text-[var(--parchment)]/85 md:text-lg">{p.description || p.tagline}</p>
          {p.launch_offer ? <LaunchOffer p={p} qty={qty} /> : null}
          {hasRepouss(p) ? <PregnancyNotice testid="product-pregnancy-notice" className="mt-6" /> : null}
          {duoOk ? <BuyChoice p={p} mode={mode} setMode={setMode} /> : null}
          {!en && (p.slug === EXTREME.slug || p.slug === "extreme-repouss") ? <ExtremePromo placement="product" testid="product-extreme-promo" className="mt-6" /> : null}
          <div id="main-buy" className={`${duoOk ? "mt-4" : "mt-10"} flex items-center gap-4`}>
            {mode !== "once" ? (
              <Link to={`/abonnement?formule=${mode}&produit=${p.slug}`} onClick={() => trackSubCta("product", mode)} data-testid="product-duo-cta" className="flex h-14 flex-1 items-center justify-center gap-2 rounded-full bg-[var(--gold)] text-xs font-semibold uppercase tracking-[0.2em] text-[var(--ink)] transition-[background-color,transform] duration-300 hover:bg-[var(--gold-soft)] active:scale-[0.98]">
                {mode === "duo" ? "Choisir l'offre Duo" : "Choisir l'offre Famille"} <ArrowRight className="h-4 w-4" />
              </Link>
            ) : <>
            <div className="flex h-14 items-center rounded-full border border-[var(--line)]">
              <button data-testid="product-qty-decrease" aria-label={t("Diminuer la quantité")} onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-4"><Minus className="h-4 w-4" /></button>
              <span data-testid="product-qty" className="w-8 text-center">{qty}</span>
              <button data-testid="product-qty-increase" aria-label={t("Augmenter la quantité")} onClick={() => setQty((q) => q + 1)} className="px-4"><Plus className="h-4 w-4" /></button>
            </div>
            <button data-testid="product-add-to-cart" onClick={() => add(p, qty)} className="flex h-14 flex-1 items-center justify-center rounded-full bg-[var(--gold)] text-xs font-semibold uppercase tracking-[0.24em] text-[var(--ink)] transition-[background-color,transform] duration-300 hover:bg-[var(--gold-soft)] active:scale-[0.98]">
              {p.preorder ? (en ? "Pre-order" : "Précommander") : t("Ajouter au panier")} · {euro(p.price * qty)}
            </button>
            </>}
          </div>
          {mode !== "once" ? <p data-testid="product-duo-note" className="mt-3 text-xs text-[var(--parchment)]/80">{mode === "duo" ? "Vous choisirez votre 2e soin à l'étape suivante. " : "Vous choisirez vos 3 autres soins à l'étape suivante. "}Prélèvement mensuel, sans engagement : pause ou arrêt depuis votre espace. <Link to="/abonnement-economies" className="text-[var(--gold-soft)] underline underline-offset-4">Voir les économies</Link></p> : null}
          {en || p.preorder ? null : <DeliveryEstimate />}
          {en || duoOk || p.preorder ? null : <SubProductBox p={p} />}
          {p.category === "Pousse" && !en ? <ExpertiseBlock testid="product-expertise" compact /> : null}
          {en ? (
            <ul data-testid="product-reassurance" className="mt-6 grid gap-3 text-xs text-[var(--parchment)]/80 sm:grid-cols-2">
              <li className="flex items-center gap-2"><Truck className="h-4 w-4 shrink-0 text-[var(--gold)]" /> UK &amp; Switzerland: €9.90, free from €79 · 4-8 days</li>
              <li className="flex items-center gap-2"><Truck className="h-4 w-4 shrink-0 text-[var(--gold)]" /> USA, Canada, Africa, worldwide: from €12.90, free from €119 · 5-15 days</li>
              <li className="flex items-center gap-2"><Truck className="h-4 w-4 shrink-0 text-[var(--gold)]" /> Europe: Mondial Relay pickup point €4.99</li>
              <li className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 shrink-0 text-[var(--gold)]" /> {t("Paiement 100 % sécurisé (3D Secure)")} · Visa, Mastercard, PayPal</li>
              <li className="flex items-center gap-2"><Check className="h-4 w-4 shrink-0 text-[var(--gold)]" /> {t("Email de confirmation & suivi du colis")}</li>
              <li className="flex items-center gap-2"><Check className="h-4 w-4 shrink-0 text-[var(--gold)]" /> {t("Fabriqué en France")} · prices in euros</li>
            </ul>
          ) : (
          <ul data-testid="product-reassurance" className="mt-6 grid gap-3 text-xs text-[var(--parchment)]/80 sm:grid-cols-2">
            <li className="flex items-center gap-2"><Truck className="h-4 w-4 shrink-0 text-[var(--gold)]" /> Point Relais / Locker offert en France</li>
            <li className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 shrink-0 text-[var(--gold)]" /> Paiement 100 % sécurisé (3D Secure)</li>
            <li className="flex items-center gap-2"><Check className="h-4 w-4 shrink-0 text-[var(--gold)]" /> {SHOP.shipDelay}</li>
            <li className="flex items-center gap-2"><Check className="h-4 w-4 shrink-0 text-[var(--gold)]" /> Fabriqué en France</li>
            <li className="flex items-center gap-2 sm:col-span-2"><Palmtree className="h-4 w-4 shrink-0 text-[var(--gold)]" /> Domicile dès 12,90 € · Europe en Point Relais 4,99 € · DOM-TOM dès 11,90 €</li>
          </ul>
          )}
          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-3 text-xs text-[var(--parchment)]/80">
            <PaymentLogos testid="product-payment-logos" />
            <span>{t("Une question ?")} <a href={`tel:${SHOP.tel}`} data-testid="product-phone" className="text-[var(--gold-soft)] underline underline-offset-4">{SHOP.phone}</a></span>
          </div>
          {en ? null : <CoachProductBox />}
          {p.contents?.length > 0 && !p.components?.length && (
            <ul data-testid="product-contents" className="mt-8 space-y-3 border-t border-[var(--line)] pt-6">
              {p.contents.map((c) => (
                <li key={c} className="flex items-center gap-3 text-sm"><Check className="h-4 w-4 text-[var(--gold)]" />{c}</li>
              ))}
            </ul>
          )}
          {p.actives?.length ? <SerumDetails p={p} /> : <ProductDetails p={p} />}
          {en ? null : <ProductGuides slug={p.slug} />}
          <WhatsAppAdvice productName={p.name} />
          <FormationProductBox slug={p.slug} />
        </div>
      </div>
      <ProductReviews slug={p.slug} />
      {en ? <div className="-mx-5 md:-mx-10"><ShopReviewsHighlight /></div> : null}
      {p.category === "Pousse" && !en ? <Testimonials className="-mx-5 mt-20 md:-mx-10" /> : null}
      {en ? null : <div className="mx-auto max-w-7xl px-5 md:px-10"><ReviewForm slug={p.slug} /></div>}
      <Related p={p} />
      <StickyBuyBar p={p} targetId="main-buy" onAdd={() => add(p, qty)} label={p.preorder ? (en ? "Pre-order" : "Précommander") : undefined} />
    </main>
  );
}
